import React from 'react';
import {
  Box,
  Text,
  VStack,
  Heading,
  Grid,
  HStack
} from '@chakra-ui/react';
import { useTeam, useSettings } from '../hooks/useApi';
import { EngineersPageSkeleton } from '../components/SkeletonComponents';

const Engineers = () => {
  const { data: team, loading: teamLoading, error: teamError } = useTeam();
  const { data: settings } = useSettings();

  // Loading state - show skeleton
  if (teamLoading) {
    return <EngineersPageSkeleton />;
  }

  // Error state
  if (teamError) {
    return (
      <Box py={{ base: 16, md: 24, lg: 32 }} px={4}>
        <VStack spacing={12} maxW="5xl" mx="auto">
          <Heading size={{ base: "xl", md: "2xl", lg: "4xl" }} textTransform="uppercase" letterSpacing="wide" textAlign="center">
            OUR ENGINEERING TEAM
          </Heading>
          <Box bg="red.900" border="1px solid" borderColor="red.600" p={6} borderRadius="md" w="100%">
            <VStack spacing={3}>
              <Text color="red.300" fontSize={{ base: "md", md: "lg" }} fontWeight="bold">
                ⚠️ Error Loading Team
              </Text>
              <Text color="white" textAlign="center">
                {teamError}
              </Text>
              <Text color="gray.300" textAlign="center" fontSize={{ base: "xs", md: "sm" }}>
                Make sure your Django backend is running on localhost:8000
              </Text>
            </VStack>
          </Box>
        </VStack>
      </Box>
    );
  }

  return (
    <Box py={{ base: 16, md: 24, lg: 32 }} px={4}>
      <VStack spacing={{ base: 16, md: 20, lg: 22 }} maxW="5xl" mx="auto">
        {/* Hero Section */}
        <VStack spacing={{ base: 8, md: 12 }} textAlign="center">
          <Heading size={{ base: "xl", md: "2xl", lg: "4xl" }} textTransform="uppercase" letterSpacing="wide">
            {settings?.engineers_page_title || "OUR ENGINEERING TEAM"}
          </Heading>
          <Box
            fontSize={{ base: "md", md: "lg", lg: "xl" }}
            color="gray.300"
            maxW="4xl"
            lineHeight="1.8"
            dangerouslySetInnerHTML={{
              __html: settings?.engineers_page_description ||
                "Meet the talented engineers behind Clover Ridge Technologies. Our remote team brings together diverse expertise in modern web development, cloud infrastructure, and system architecture."
            }}
            sx={{
              '& p': { mb: 4 },
              '& ul': { pl: 6, mb: 4 },
              '& ol': { pl: 6, mb: 4 },
              '& li': { mb: 2 },
              '& h1, & h2, & h3, & h4, & h5, & h6': {
                color: 'white',
                fontWeight: 'bold',
                mb: 3,
                mt: 6
              },
              '& strong': { color: 'white' },
              '& a': { color: 'green.400', textDecoration: 'underline' }
            }}
          />
        </VStack>

        {/* Team Grid */}
        <Box py={{ base: 12, md: 16 }} w="100%">
          {team && team.length > 0 ? (
            <Grid templateColumns={{ base: "1fr", lg: "repeat(auto-fit, minmax(450px, 1fr))" }} gap={{ base: 8, md: 12 }} w="100%">
              {team.map((member) => (
                <Box
                  key={member.id}
                  bg="gray.900"
                  p={{ base: 6, md: 8, lg: 12 }}
                  border="1px solid"
                  borderColor="gray.700"
                  _hover={{ bg: "gray.800" }}
                  transition="all 0.3s"
                  minH={{ base: "auto", lg: "500px" }}
                >
                  <VStack align="start" spacing={{ base: 6, md: 8 }}>
                    {/* Avatar */}
                    <Box
                      w={{ base: 16, md: 20, lg: 60 }}
                      h={{ base: 16, md: 20, lg: 60 }}
                      bg={member.headshot ? "transparent" : "gray.800"}
                      border="2px solid"
                      borderColor="green.500"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                      borderRadius="md"
                      overflow="hidden"
                    >
                      {member.headshot ? (
                        <img
                          src={member.headshot}
                          alt={member.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <Text>👨‍💻</Text>
                      )}
                    </Box>

                    <VStack align="start" spacing={{ base: 4, md: 6 }} w="100%">
                      <VStack align="start" spacing={2}>
                        <Heading size={{ base: "md", md: "lg", lg: "xl" }} textTransform="uppercase" letterSpacing="wide">
                          {member.name}
                        </Heading>
                        <Text color="green.500" fontSize={{ base: "sm", md: "md" }} textTransform="uppercase" letterSpacing="wide" fontWeight="bold">
                          {member.role}
                        </Text>
                      </VStack>

                      {/* Bio */}
                      <Box color="gray.300" lineHeight="1.8" fontSize={{ base: "sm", md: "md" }} flex="1">
                        <div dangerouslySetInnerHTML={{ __html: member.bio }} />
                      </Box>

                      {/* Social Links */}
                      {(member.email || member.linkedin_url || member.github_url || member.twitter_url) && (
                        <Box pt={4} w="100%">
                          <VStack align="start" spacing={4} w="100%">
                            <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="bold" textTransform="uppercase" letterSpacing="wide" color="gray.400">
                              CONNECT
                            </Text>
                            <HStack spacing={{ base: 4, md: 6 }} flexWrap="wrap">
                              {member.email && (
                                <Box 
                                  as="a" 
                                  href={`mailto:${member.email}`} 
                                  color="green.500" 
                                  fontSize={{ base: "xs", md: "sm" }} 
                                  _hover={{ color: "green.400" }}
                                  textDecoration="underline"
                                >
                                  Email
                                </Box>
                              )}
                              {member.linkedin_url && (
                                <Box 
                                  as="a" 
                                  href={member.linkedin_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  color="green.500" 
                                  fontSize={{ base: "xs", md: "sm" }} 
                                  _hover={{ color: "green.400" }}
                                  textDecoration="underline"
                                >
                                  LinkedIn
                                </Box>
                              )}
                              {member.github_url && (
                                <Box 
                                  as="a" 
                                  href={member.github_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  color="green.500" 
                                  fontSize={{ base: "xs", md: "sm" }} 
                                  _hover={{ color: "green.400" }}
                                  textDecoration="underline"
                                >
                                  GitHub
                                </Box>
                              )}
                              {member.twitter_url && (
                                <Box 
                                  as="a" 
                                  href={member.twitter_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  color="green.500" 
                                  fontSize={{ base: "xs", md: "sm" }} 
                                  _hover={{ color: "green.400" }}
                                  textDecoration="underline"
                                >
                                  Twitter
                                </Box>
                              )}
                            </HStack>
                          </VStack>
                        </Box>
                      )}
                    </VStack>
                  </VStack>
                </Box>
              ))}
            </Grid>
          ) : (
            /* No Team Members State */
            <VStack spacing={12} textAlign="center" w="100%">
              <Box bg="gray.900" p={{ base: 8, md: 12, lg: 16 }} border="1px solid" borderColor="gray.700">
                <VStack spacing={8}>
                  <Text fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}>👥</Text>
                  <Heading size={{ base: "md", md: "lg", lg: "xl" }} textTransform="uppercase" letterSpacing="wide">
                    NO TEAM MEMBERS YET
                  </Heading>
                  <Text color="gray.300" fontSize={{ base: "md", md: "lg" }} lineHeight="1.8">
                    Team member profiles will appear here once they're added to your Django CMS.
                  </Text>
                </VStack>
              </Box>
            </VStack>
          )}
        </Box>

        {/* Team Philosophy */}
        <Box py={{ base: 12, md: 16 }} w="100%">
          <VStack spacing={{ base: 8, md: 12 }} w="100%" textAlign="center">
            <Heading size={{ base: "lg", md: "xl", lg: "3xl" }} textTransform="uppercase" letterSpacing="wide">
              {settings?.team_philosophy_title || "OUR PHILOSOPHY"}
            </Heading>
            <Box
              fontSize={{ base: "md", md: "lg", lg: "xl" }}
              color="gray.300"
              maxW="4xl"
              lineHeight="1.8"
              dangerouslySetInnerHTML={{
                __html: settings?.team_philosophy_description ||
                  "We believe in collaborative engineering, continuous learning, and delivering solutions that exceed expectations. Our remote-first culture enables us to work with the best talent regardless of location, bringing diverse perspectives to every project."
              }}
              sx={{
                '& p': { mb: 4 },
                '& ul': { pl: 6, mb: 4 },
                '& ol': { pl: 6, mb: 4 },
                '& li': { mb: 2 },
                '& h1, & h2, & h3, & h4, & h5, & h6': {
                  color: 'white',
                  fontWeight: 'bold',
                  mb: 3,
                  mt: 6
                },
                '& strong': { color: 'white' },
                '& a': { color: 'green.400', textDecoration: 'underline' }
              }}
            />
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
};

export default Engineers;